/**
 * notificationStreamStore — singleton de la conexión SSE de notificaciones.
 *
 * Una sola conexión por sesión del browser (Fase 7.h): con una por
 * componente, los re-mounts y la rotación del token acumulaban 3-6
 * conexiones vivas; el límite de 6 por origen de HTTP/1.1 encolaba el resto
 * de las peticiones y la UI parecía colgada. Los componentes no abren la
 * conexión: se suscriben con `subscribe(listener)`.
 *
 * El stream se lee con `fetch` y el token va en `Authorization` (OT-0084).
 * Con `EventSource` iba en `?access_token=` y quedaba escrito en los logs del
 * backend. `fetch` no reconecta solo, así que el store lo hace:
 *   - Espera creciente entre intentos (1 s → 30 s), que vuelve a 1 s en
 *     cuanto el backend confirma la conexión (`connected`).
 *   - Cada intento pide un token con `getToken`: una reconexión horas
 *     después no sale con el token vencido. Por lo mismo, una rotación del
 *     token no reabre la conexión; el backend sólo lo valida al abrir.
 *   - Un 4xx al abrir (token rechazado, tenant suspendido) no se reintenta:
 *     el badge sigue con su polling y el siguiente `connect` lo vuelve a
 *     intentar.
 *
 * Logs: `[sse]` en dev, para auditar fugas de conexiones.
 */

import { create } from 'zustand'

import { ApiError, apiStream } from '../api/client'
import type { NotificationItem } from '../api/notifications'

type NotificationListener = (n: NotificationItem) => void

/** Token para abrir (o reabrir) la conexión. */
export type StreamTokenProvider = () => Promise<string>

type NotificationStreamEvent =
  | { type: 'connected'; payload: unknown }
  | { type: 'notification'; payload: NotificationItem }

const STREAM_PATH = '/api/v1/me/notifications/stream'
export const RETRY_INITIAL_MS = 1_000
export const RETRY_MAX_MS = 30_000

interface NotificationStreamState {
  /** Corta la conexión activa y su espera de reconexión; null sin conexión. */
  controller: AbortController | null
  /** Da el token de cada intento; lo fija el último `connect`. */
  getToken: StreamTokenProvider | null
  /** Subscribers a eventos `notification`. */
  listeners: Set<NotificationListener>
  /** Abre la conexión si no hay una. Idempotente. */
  connect: (getToken: StreamTokenProvider) => void
  /** Cierra la conexión y limpia listeners. */
  disconnect: () => void
  /** Suscribe un listener. Retorna unsubscribe. */
  subscribe: (listener: NotificationListener) => () => void
}

/** Espera `ms`, o menos si `signal` se aborta. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer)
      signal.removeEventListener('abort', done)
      resolve()
    }
    const timer = setTimeout(done, ms)
    signal.addEventListener('abort', done)
  })
}

export const useNotificationStreamStore = create<NotificationStreamState>(
  (set, get) => {
    const dispatch = (notification: NotificationItem) => {
      // Snapshot del set para tolerar listeners que se desuscriban
      // durante el dispatch.
      for (const listener of Array.from(get().listeners)) {
        try {
          listener(notification)
        } catch (err) {
          console.error('[sse] listener threw', err)
        }
      }
    }

    const run = async (controller: AbortController) => {
      const { signal } = controller
      let delay = RETRY_INITIAL_MS
      while (!signal.aborted) {
        const { getToken } = get()
        if (getToken === null) break
        try {
          const accessToken = await getToken()
          // Sin eventos terminales: el stream no termina por sí solo, y si
          // se corta `apiStream` lanza `STREAM_INTERRUPTED`.
          for await (const event of apiStream<NotificationStreamEvent>(STREAM_PATH, {
            accessToken,
            method: 'GET',
            signal,
            terminal: [],
          })) {
            if (event.type === 'connected') {
              delay = RETRY_INITIAL_MS
              if (import.meta.env.DEV) console.debug('[sse] connected')
            } else if (event.type === 'notification') {
              dispatch(event.payload)
            }
          }
        } catch (err) {
          if (signal.aborted) break
          if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
            console.warn('[sse] stream rejected, not retrying', { status: err.status })
            break
          }
          if (import.meta.env.DEV) {
            console.debug('[sse] connection lost, retrying', { delayMs: delay })
          }
        }
        await sleep(delay, signal)
        delay = Math.min(delay * 2, RETRY_MAX_MS)
      }
      // Libera el lugar para que el siguiente `connect` abra de nuevo, salvo
      // que un `disconnect` + `connect` ya haya puesto otra conexión.
      if (get().controller === controller) set({ controller: null })
    }

    return {
      controller: null,
      getToken: null,
      listeners: new Set(),

      connect: (getToken: StreamTokenProvider) => {
        set({ getToken })
        if (get().controller !== null) return
        const controller = new AbortController()
        set({ controller })
        if (import.meta.env.DEV) console.debug('[sse] opening stream')
        void run(controller)
      },

      disconnect: () => {
        const { controller } = get()
        if (controller !== null) {
          if (import.meta.env.DEV) console.debug('[sse] disconnect — closing stream')
          controller.abort()
        }
        set({ controller: null, getToken: null, listeners: new Set() })
      },

      subscribe: (listener: NotificationListener) => {
        const { listeners } = get()
        listeners.add(listener)
        // Trigger re-render del state al cambiar el set (Zustand compara
        // por referencia — clonamos).
        set({ listeners: new Set(listeners) })
        return () => {
          const current = get().listeners
          current.delete(listener)
          set({ listeners: new Set(current) })
        }
      },
    }
  },
)
